<template>
  <div class="events-omnisearch">
    <sp-quantum-select
      ref="select"
      :aria-label="placeholder"
      class="events-omnisearch__select"
      navigation="flat"
      max-height="var(--sp-popup-available-height, 25rem)"
      fuzzy-search
      section-order="relevance"
      no-substring-fallback
      location="bottom-left"
      start-on-root
      close-on-select
      hide-search-bar
      show-search-bar-in-sheet
      :items.prop="models"
      :search="searchQuery"
      :open="open"
      :loading="loading"
      :selected.prop="selectedValues"
      :highlight-first-result="showHint"
      :highlight-idle-first-result="showHint"
      exit-start
      @update-selected-item="handleUpdateSelectedItem"
      @update-search="handleInput"
      @update-open="handleUpdateOpen"
      @search-enter="handleKeyDownEnter"
      @exit-start="handleExitStart"
      @focusin="handleSelectFocusIn"
      @focusout="handleSelectFocusOut"
    >
      <sp-text-field
        slot="activator"
        ref="input"
        v-model="inputText"
        class="events-omnisearch__input"
        variant="plain"
        prepend-icon="search-bold-filled"
        autocomplete="off"
        :input-test-id="inputTestId"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="isReadonly"
        @input="handleInput"
        @keydown="handleKeyDown"
        @keydown.enter.stop="handleKeyDownEnter"
        @keydown.space.stop
        @click.stop="handleFieldClick"
        @focusin.self="handleFocusIn"
        @focusout.self="handleFieldFocusOut"
      ></sp-text-field>
    </sp-quantum-select>
  </div>
</template>

<script setup>
/**
 * The events search box: a single field over the events autocomplete corpus
 * (schedules, categories, locations) on the sp-quantum-select stack. It is
 * form-associated — a free-typed query submits as this element's own form
 * value, while a picked suggestion instead stages its filters as hidden
 * light-DOM inputs (or navigates directly for schedule rows).
 *
 * @displayName EventsOmnisearch
 * @group Custom Elements
 * @component sp-events-omnisearch
 */
import { computed, toRef, useTemplateRef } from "vue";
import { isPresent } from "#ce/utils/props";
import { formAssociatedHost } from "#ce/framework/utils";
import { useFormControl } from "#ce/composables/useFormControl";
import { useQuantumSearchField } from "#ce/shared/filters/composables/useQuantumSearchField";
import { useEventsOmnisearchData } from "./composables/useEventsOmnisearchData";
import { useEventsOmnisearchQuery } from "./composables/useEventsOmnisearchQuery";
import { useEventsOmnisearchInteraction } from "./composables/useEventsOmnisearchInteraction";
import { useEventsOmnisearchContribution } from "./composables/useEventsOmnisearchContribution";
import { apologyItem } from "./events-quantum-items.js";
import { props as eventsOmnisearchProps } from "./props.js";

defineOptions(formAssociatedHost());
const props = defineProps(eventsOmnisearchProps);
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });
const isReadonly = computed(() => isPresent(props.readonly));
const editable = computed(() => !disabled.value && !isReadonly.value);
const input = useTemplateRef("input");
const select = useTemplateRef("select");

const field = useQuantumSearchField();
const { inputText, searchQuery, open } = field;
const query = useEventsOmnisearchQuery({ props, disabled, field });
const { selectedValues, commitFreeTextIfChanged: handleFieldFocusOut } = query;
const {
  showHint,
  reveal,
  handleSelectFocusIn,
  handleSelectFocusOut,
  handleKeyDown,
  handleInput,
  handleUpdateOpen,
  handleExitStart,
  handleFocusIn,
  handleFieldClick,
  handleKeyDownEnter,
  handleUpdateSelectedItem,
} = useEventsOmnisearchInteraction({ field, query, input, select, editable });

useEventsOmnisearchContribution({ props, query, editable, reveal });

const { idleItems, searchItems, loading } = useEventsOmnisearchData(() => props.src);
const models = computed(() => {
  const search = (searchQuery.value ?? "").trim();
  return search ? [...searchItems.value, apologyItem(search)] : idleItems.value;
});
</script>

<style>
:host {
  display: block;
  width: 100%;

  /* The dropdown spans its own input (the popup anchor) and hugs its content,
     matching the filter-bar dropdowns beside it. */
  --sp-comp-popup-width: 100%;
  --sp-comp-popup-min-width: 100%;
  --sp-comp-quantum-select-popup-min-height: 0;
}
</style>

<style lang="scss" scoped>
.events-omnisearch,
.events-omnisearch__select,
.events-omnisearch__input {
  width: 100%;
}

// The plain text-field variant zeroes every inset. Inset the content and gap
// the icon so the text starts 3rem in (0.5rem + 2rem icon + 0.5rem), the
// legacy input's text offset.
.events-omnisearch__input {
  --sp-comp-text-field-content-padding-inline: var(--sp-ref-scale-8, 0.5rem);
}

// External part rules can't pierce this component's nested shadow, so without
// this the leading magnifier falls through to the sp-icon default (brand
// primary).
.events-omnisearch__input::part(prepend-icon) {
  --fill: var(--sp-comp-omni-search-icon-fill, var(--sp-comp-filter-default-activator-icon-fill-color));
  margin-inline-end: var(--sp-ref-scale-8, 0.5rem);
}

/* Center the field vertically when the host is taller than the field (e.g.
   the grouped events search bar) — the quantum-select wrapper sizes to its
   content and relies on the parent layout for vertical centering. */
.events-omnisearch {
  height: 100%;
  display: flex;
  align-items: center;
}
</style>
